import type { GanttParams, TaskListParams, TaskStatus } from '@/api/types'
import { TASK_PRIORITY_ORDER, TASK_STATUS_ORDER } from '@/lib/orders'
import { RETURN_SHAPE } from '@/lib/return-to'
import { exactly, flag, integer, oneOf, orElse, readSearch, text } from '@/lib/search-params'

const STATUSES = [...TASK_STATUS_ORDER, 'cancelled'] as const

/**
 * Paramètres de recherche de `/tasks`, partagés avec l'onglet Tâches client.
 * Tout l'état des filtres vit dans l'URL : la vue est partageable et survit
 * à un rechargement.
 */
const SHAPE = {
  /** #224 : l'origine d'un panneau de tâche ouvert depuis une fiche. */
  ...RETURN_SHAPE,
  vue: orElse(oneOf(['liste', 'kanban', 'gantt'] as const), 'liste' as const),
  q: text(),
  client: text(),
  assigne: text(),
  /** Liste de statuts séparés par des virgules, comme l'attend le contrat. */
  statuts: text(),
  priorite: oneOf(TASK_PRIORITY_ORDER),
  etiquette: text(),
  /** Origine de la tâche. */
  origine: oneOf(['web', 'mcp', 'cli', 'api', 'portal'] as const),
  /**
   * Les demandes du client (lot 10) — déposées au portail ou notées lors d'un
   * échange. Porté dans l'URL pour que « les 43 demandes en attente » du
   * tableau de bord mènent quelque part : un compte qui annonce des lignes sans
   * donner le moyen de les voir est exactement le défaut que `client_requests`
   * corrige.
   */
  demandes: exactly(true),
  terminees: flag(),
  /**
   * Projet : référence `CLIENT:CODE` (lisible dans l'URL, acceptée par le
   * serveur partout où il attend `project_id`), ou `none` pour les tâches
   * sans projet.
   */
  projet: text(),
  /**
   * Sprint (lot 9) : `actif` — le sprint en cours de chaque projet —,
   * `aucun` — hors sprint —, ou l'identifiant d'un sprint.
   */
  sprint: text(),
  /**
   * Livrable (lot 12) : une clé « L-3 », une référence complète, ou `aucun`
   * pour les tâches qui n'en ont pas.
   */
  livrable: text(),
  /**
   * `schedule` (contrat 1.36.9, arbitrage de Marc) : l'ordre de lecture de
   * toute liste de tâches — sans projet d'abord, puis par projet ; dans chaque
   * groupe les planifiées par date, puis les non planifiées. C'est le tri par
   * défaut, et le seul sous lequel la liste groupe ses lignes par projet.
   */
  tri: orElse(
    oneOf(['schedule', 'position', '-updated_at', 'due_date', 'priority', '-created_at'] as const),
    'schedule' as const,
  ),
  /** `oui` : planifiées (un début ou une échéance) ; `non` : ni l'un ni l'autre (1.36.9). */
  planif: oneOf(['oui', 'non'] as const),
  page: orElse(integer({ min: 1 }), 1),
  /** Ouvre le dialogue de création — utilisé par la palette ⌘K. */
  nouvelle: flag(),
}

export type TasksSearch = ReturnType<typeof parseTasksSearch>

export function parseTasksSearch(search: Record<string, unknown>) {
  return readSearch(SHAPE, search)
}

/** Paramètres minimaux pour construire un lien vers `/tasks`. */
export const DEFAULT_TASKS_SEARCH = {
  vue: 'liste',
  tri: 'schedule',
  page: 1,
} satisfies Pick<TasksSearch, 'vue' | 'tri' | 'page'>

export function parseStatuses(value: string | undefined): TaskStatus[] {
  if (!value) return []
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item): item is TaskStatus => (STATUSES as readonly string[]).includes(item))
}

export function serializeStatuses(statuses: TaskStatus[]): string | undefined {
  if (statuses.length === 0) return undefined
  // Ordre stable : l'URL ne change pas selon l'ordre des clics.
  return STATUSES.filter((status) => statuses.includes(status)).join(',')
}

/** Valeur d'URL du filtre Livrable → `deliverable_id` du serveur. */
export function toDeliverableParam(value: string | undefined): string | undefined {
  if (value === 'aucun') return 'none'
  return value === '' ? undefined : value
}

/** Valeur d'URL du filtre Sprint → paramètre `sprint_id` du serveur. */
export function toSprintParam(value: string | undefined): string | undefined {
  if (value === 'actif') return 'active'
  if (value === 'aucun') return 'none'
  return value === '' ? undefined : value
}

/**
 * L'interrupteur « Terminées » n'existe qu'en Liste (#158). Kanban et Gantt
 * montrent toujours les terminées : leur colonne et leurs barres étaient vides
 * par défaut, alors que c'est là qu'on vient voir ce qui a été fait.
 *
 * Ces deux vues **ignorent** `terminees` sans l'effacer de l'URL : revenir en
 * Liste retrouve l'interrupteur tel qu'on l'a laissé.
 */
export function showsDoneToggle(search: Pick<TasksSearch, 'vue'>): boolean {
  return search.vue === 'liste'
}

/**
 * La Liste montre-t-elle les sous-tâches ? (#166)
 *
 * Par défaut non : seulement les racines, filtrées **au serveur**
 * (`parent_id=none`) pour que total et pagination restent justes. Une tâche
 * mère porte déjà son compte de sous-tâches, qui mène à elles.
 *
 * Mais une **recherche** ou un **filtre de personne** les fait apparaître :
 * sans cela, chercher le titre d'une sous-tâche ne la trouverait nulle part,
 * et une sous-tâche confiée à quelqu'un disparaîtrait de « Mes tâches » — la
 * personne ne verrait plus ce qu'on lui a demandé.
 */
export function listShowsSubtasks(search: Pick<TasksSearch, 'q' | 'assigne'>): boolean {
  return Boolean(search.q?.trim()) || Boolean(search.assigne)
}

/**
 * Traduit les paramètres d'URL en query pour `GET /tasks` — **vue Liste**.
 * Le kanban a ses deux requêtes à lui (`toKanbanParams`).
 */
export function toTaskListParams(
  search: TasksSearch,
  pageSize: number,
  clientId?: string,
  projectId?: string,
  sprintId?: string,
): TaskListParams {
  const statuses = parseStatuses(search.statuts)

  return {
    ...commonTaskFilters(search, clientId, projectId, sprintId),
    status: statuses.length > 0 ? statuses.join(',') : undefined,
    include_done: search.terminees ?? false,
    parent_id: listShowsSubtasks(search) ? undefined : 'none',
    planned: plannedParam(search),
    sort: search.tri,
    page: search.page,
    page_size: pageSize,
  }
}

/** Le filtre « planifiées / non planifiées », absent quand on ne tranche pas. */
export function plannedParam(search: Pick<TasksSearch, 'planif'>): boolean | undefined {
  return search.planif === undefined ? undefined : search.planif === 'oui'
}

function commonTaskFilters(
  search: TasksSearch,
  clientId?: string,
  projectId?: string,
  sprintId?: string,
) {
  return {
    client_id: clientId ?? search.client ?? undefined,
    project_id: projectId ?? search.projet ?? undefined,
    sprint_id: sprintId ?? toSprintParam(search.sprint),
    deliverable_id: toDeliverableParam(search.livrable),
    q: search.q ?? undefined,
    assignee_id: search.assigne ?? undefined,
    priority: search.priorite,
    label: search.etiquette ?? undefined,
    created_via: search.origine ?? undefined,
    client_request: search.demandes,
  }
}

/** Plafond de chargement des colonnes en cours du kanban ; au-delà, un bandeau prévient. */
export const KANBAN_PAGE_SIZE = 200

/**
 * Terminées chargées au kanban : **les plus récentes seulement**. Elles
 * s'accumulent sans fin ; bornées à part, elles ne peuvent jamais pousser une
 * tâche en cours hors du tableau.
 */
export const KANBAN_DONE_LIMIT = 30

const OPEN_STATUSES: readonly TaskStatus[] = ['backlog', 'todo', 'in_progress', 'in_review']

/**
 * Les deux requêtes du kanban.
 *
 * - **En cours** : les quatre colonnes ouvertes, ordonnées par `position`,
 *   jusqu'à `KANBAN_PAGE_SIZE`. Les terminées n'y entrent jamais, quel que soit
 *   leur nombre : c'est ce qui garantit qu'aucune tâche ouverte ne disparaît.
 * - **Terminées** : `done`, les `KANBAN_DONE_LIMIT` plus récemment modifiées
 *   (`-updated_at` ; le contrat n'offre pas de tri sur `completed_at`).
 *
 * Un filtre de statuts restreint chaque requête à sa part ; une part vide vaut
 * `null` et la requête ne part pas. `cancelled` n'a pas de colonne.
 */
export function toKanbanParams(
  search: TasksSearch,
  clientId?: string,
  projectId?: string,
  sprintId?: string,
): { open: TaskListParams | null; done: TaskListParams | null } {
  const wanted = parseStatuses(search.statuts)
  const pick = (statuses: readonly TaskStatus[]) =>
    wanted.length === 0 ? [...statuses] : statuses.filter((status) => wanted.includes(status))

  const open = pick(OPEN_STATUSES)
  const done = pick(['done'])
  // « Non planifiées » est un filtre de premier rang sur toutes les surfaces (1.36.9) ; le Gantt, lui, garde `include_undated`.
  const filters = {
    ...commonTaskFilters(search, clientId, projectId, sprintId),
    planned: plannedParam(search),
  }

  return {
    open:
      open.length === 0
        ? null
        : {
            ...filters,
            status: open.join(','),
            include_done: false,
            sort: 'position',
            page: 1,
            page_size: KANBAN_PAGE_SIZE,
          },
    done:
      done.length === 0
        ? null
        : {
            ...filters,
            status: 'done',
            include_done: true,
            sort: '-updated_at',
            page: 1,
            page_size: KANBAN_DONE_LIMIT,
          },
  }
}

/** Y a-t-il au moins un filtre actif (hors vue, tri et page) ? */
export function hasActiveFilters(search: TasksSearch): boolean {
  // `terminees` ne compte que là où son interrupteur se voit : ailleurs,
  // « Réinitialiser » s'allumerait pour un filtre invisible.
  return !!(
    search.q ??
    search.client ??
    search.assigne ??
    search.statuts ??
    search.priorite ??
    search.etiquette ??
    search.origine ??
    search.demandes ??
    search.projet ??
    search.sprint ??
    search.livrable ??
    search.planif ??
    (showsDoneToggle(search) ? search.terminees : undefined)
  )
}

/**
 * Ce que « Réinitialiser » remet à zéro. Hors Liste, `terminees` est laissé tel
 * quel : effacer un réglage qu'on ne voit pas surprendrait au retour en Liste.
 */
export function resetFilters(search: Pick<TasksSearch, 'vue'>): Partial<TasksSearch> {
  if (showsDoneToggle(search)) return EMPTY_FILTERS
  const { terminees: _kept, ...rest } = EMPTY_FILTERS
  return rest
}

export const EMPTY_FILTERS: Partial<TasksSearch> = {
  q: undefined,
  client: undefined,
  assigne: undefined,
  statuts: undefined,
  priorite: undefined,
  etiquette: undefined,
  origine: undefined,
  demandes: undefined,
  projet: undefined,
  sprint: undefined,
  livrable: undefined,
  planif: undefined,
  terminees: undefined,
  page: 1,
}

/**
 * Traduit les mêmes filtres d'URL en query pour `GET /gantt`, avec la même
 * sémantique que `/tasks` (contrat 1.3). Seuls le tri et la pagination n'ont
 * pas de sens ici : le diagramme montre toute la fenêtre d'un coup.
 */
export function toGanttParams(
  search: TasksSearch,
  fixedClientId?: string,
  fixedProjectId?: string,
  fixedSprintId?: string,
): GanttParams {
  // Une recherche vide ou blanche ne doit pas partir : le serveur la traiterait
  // comme un filtre et ne renverrait rien.
  const q = search.q?.trim()

  return {
    client_id: fixedClientId ?? search.client,
    assignee_id: search.assigne,
    client_request: search.demandes,
    label: search.etiquette,
    q: q === '' ? undefined : q,
    status: serializeStatuses(parseStatuses(search.statuts)),
    priority: search.priorite,
    // Toujours : le Gantt montre ce qui a été fait (#158), sans interrupteur.
    include_done: true,
    // Le volet « à planifier » a besoin des tâches sans date.
    include_undated: true,
    project_id: fixedProjectId ?? search.projet,
    sprint_id: fixedSprintId ?? toSprintParam(search.sprint),
    deliverable_id: toDeliverableParam(search.livrable),
  }
}
